// src/components/Featured/FeatureOfferButton.jsx
// Botón "Destacar oferta": el vendedor paga un fee de servicio y la oferta sube al tope.
// DOS métodos:
//  · CRIPTO (auto): paga USDC/USDT en Base o BNB con su wallet Privy → verificamos on-chain.
//  · BINANCE PAY (manual): paga a tu Binance Pay ID y sube el ID de la orden → queda pendiente
//    hasta que el fundador aprueba por email.
// Es un fee de servicio (visibilidad), NO custodia de los fondos del trade.
// Solo se monta cuando REACT_APP_PRIVY_APP_ID existe (usa hooks de Privy). Gatealo en el padre.
/* global BigInt */
import React, { useState, useEffect, useRef } from 'react';
import { FiStar, FiLoader, FiCheck, FiClock } from 'react-icons/fi';
import { usePrivy, useWallets } from '@privy-io/react-auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const CHAIN_PARAMS = {
  8453: { chainId: '0x2105', chainName: 'Base', nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, rpcUrls: ['https://mainnet.base.org'], blockExplorerUrls: ['https://basescan.org'] },
  56:   { chainId: '0x38',   chainName: 'BNB Smart Chain', nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 }, rpcUrls: ['https://bsc-dataseed.binance.org'], blockExplorerUrls: ['https://bscscan.com'] },
};

let _configPromise = null;
function getFeaturedConfig() {
  if (!_configPromise) _configPromise = fetch(`${API_URL}/api/featured/config`).then(r => r.json()).catch(() => ({ enabled: false, chains: [] }));
  return _configPromise;
}
function encodeTransfer(to, amountUnits) {
  return '0xa9059cbb' + to.toLowerCase().replace(/^0x/, '').padStart(64, '0') + amountUnits.toString(16).padStart(64, '0');
}
function priceUnits(price, decimals) {
  const base6 = BigInt(Math.round(Number(price) * 1e6));
  return decimals >= 6 ? base6 * (10n ** BigInt(decimals - 6)) : base6 / (10n ** BigInt(6 - decimals));
}
function pickWallet(wallets) { return (wallets && wallets.length) ? (wallets.find(w => w.walletClientType === 'privy') || wallets[0]) : null; }
function humanize(err) {
  const m = (err && err.message) || String(err || '');
  if (/user rejected|denied|4001/i.test(m)) return 'Cancelaste el pago.';
  if (/insufficient/i.test(m)) return 'No te alcanza el saldo para el pago.';
  if (/Failed to fetch/i.test(m)) return 'No se pudo conectar al servidor.';
  return m || 'No se pudo destacar la oferta.';
}
const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function FeatureOfferButton({ offer, onFeatured }) {
  const { authenticated } = usePrivy();
  const { wallets } = useWallets();
  const [cfg, setCfg]       = useState(null);
  const [method, setMethod] = useState('crypto'); // crypto | binancepay
  const [chainId, setChain] = useState(null);
  const [token, setToken]   = useState(null);
  const [reference, setRef] = useState('');
  const [status, setStatus] = useState('idle');   // idle | paying | verifying | done | bp-sending | bp-pending | error
  const [error, setError]   = useState('');
  const mounted = useRef(true);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    getFeaturedConfig().then(c => {
      if (!mounted.current) return;
      setCfg(c);
      const c0 = c.chains?.[0];
      if (c0) { setChain(c0.chainId); setToken(c0.tokens?.[0]?.symbol || null); }
    });
  }, []);

  const now = Date.now();
  const isFeatured = offer.featuredUntil && new Date(offer.featuredUntil).getTime() > now;
  if (isFeatured) {
    const hoursLeft = Math.max(1, Math.round((new Date(offer.featuredUntil).getTime() - now) / 3600000));
    return <span className="fin-chip" style={{ color: '#f0b90b', borderColor: '#f0b90b55' }}><FiStar style={{ verticalAlign: -1 }} /> Destacada · {hoursLeft} h</span>;
  }
  if (status === 'bp-pending') {
    return <span className="fin-chip" style={{ color: '#5a7a96', borderColor: '#5a7a9655' }}><FiClock style={{ verticalAlign: -1 }} /> Pago en revisión</span>;
  }
  if (cfg && !cfg.enabled && !cfg.binancePay?.enabled) return null;
  if (!cfg) return null;

  const chain = (cfg.chains || []).find(c => c.chainId === chainId) || cfg.chains?.[0];
  const tok   = chain?.tokens.find(t => t.symbol === token) || chain?.tokens?.[0];
  const busy  = status === 'paying' || status === 'verifying' || status === 'bp-sending';
  const bpOn  = !!cfg.binancePay?.enabled;

  const payCrypto = async () => {
    setError('');
    try {
      const wallet = pickWallet(wallets);
      if (!authenticated || !wallet) { setError('Conectá tu wallet para pagar.'); setStatus('error'); return; }
      if (!cfg.treasury || !chain || !tok) { setError('Cobros no configurados.'); setStatus('error'); return; }
      const from = wallet.address;
      const provider = await wallet.getEthereumProvider();
      const hex = '0x' + Number(chain.chainId).toString(16);
      try { await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] }); }
      catch (e) { if ((e?.code === 4902 || /Unrecognized chain/i.test(e?.message || '')) && CHAIN_PARAMS[chain.chainId]) await provider.request({ method: 'wallet_addEthereumChain', params: [CHAIN_PARAMS[chain.chainId]] }); }
      setStatus('paying');
      const data = encodeTransfer(cfg.treasury, priceUnits(cfg.price, tok.decimals));
      const txHash = await provider.request({ method: 'eth_sendTransaction', params: [{ from, to: tok.address, data, value: '0x0' }] });
      setStatus('verifying');
      const res = await fetch(`${API_URL}/api/offers/${offer.id}/feature`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ txHash, chainId: chain.chainId }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.message || 'No se pudo verificar el pago.');
      setStatus('done');
      onFeatured?.(d.offer || { ...offer, featuredUntil: d.featuredUntil });
    } catch (e) { setError(humanize(e)); setStatus('error'); }
  };

  const submitBinance = async () => {
    setError('');
    if (reference.trim().length < 4) { setError('Pegá el ID de tu orden de Binance Pay.'); setStatus('error'); return; }
    setStatus('bp-sending');
    try {
      const res = await fetch(`${API_URL}/api/offers/${offer.id}/feature/binancepay`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ reference: reference.trim() }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.message || 'No se pudo registrar el pago.');
      setStatus('bp-pending');
    } catch (e) { setError(humanize(e)); setStatus('error'); }
  };

  const pill = (on) => ({ border: 'none', cursor: 'pointer', borderRadius: 4, padding: '2px 7px', fontFamily: 'var(--fin-font-mono, monospace)', fontSize: '0.58rem', fontWeight: 700, background: on ? '#f0b90b' : 'transparent', color: on ? '#2a1d02' : 'var(--mk-text-secondary, #5a7a96)' });
  const box  = { display: 'inline-flex', gap: 2, background: 'var(--mk-bg-card, #f0f2f5)', borderRadius: 6, padding: 2 };
  const showPickers = !busy && status !== 'done';
  const label = status === 'paying' ? 'Pagando…' : status === 'verifying' ? 'Verificando…' : status === 'done' ? '¡Destacada!' : `Destacar · ${cfg.price} ${token || ''}`;

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 3 }}>
      {/* Elegir método (si hay Binance Pay Y cripto activos) */}
      {bpOn && cfg.enabled && showPickers && (
        <span style={box} role="group" aria-label="Método de pago">
          <button onClick={() => { setMethod('crypto'); setStatus('idle'); }} aria-pressed={method === 'crypto'} style={pill(method === 'crypto')}>Cripto</button>
          <button onClick={() => { setMethod('binancepay'); setStatus('idle'); }} aria-pressed={method === 'binancepay'} style={pill(method === 'binancepay')}>Binance Pay</button>
        </span>
      )}

      {method === 'crypto' && cfg.enabled ? (
        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          {showPickers && (cfg.chains || []).length > 1 && (
            <span style={box} role="group" aria-label="Red">
              {cfg.chains.map(c => <button key={c.chainId} onClick={() => { setChain(c.chainId); setToken(c.tokens?.[0]?.symbol || null); }} aria-pressed={chainId === c.chainId} style={pill(chainId === c.chainId)}>{c.name}</button>)}
            </span>
          )}
          {showPickers && chain && chain.tokens.length > 1 && (
            <span style={box} role="group" aria-label="Moneda">
              {chain.tokens.map(t => <button key={t.symbol} onClick={() => setToken(t.symbol)} aria-pressed={token === t.symbol} style={pill(token === t.symbol)}>{t.symbol}</button>)}
            </span>
          )}
          <button className="fin-chip" onClick={payCrypto} disabled={busy || status === 'done'} title={`Destacar ${cfg.hours} h pagando ${cfg.price} ${token} en ${chain?.name}`} style={{ cursor: busy ? 'wait' : 'pointer', color: '#f0b90b', borderColor: '#f0b90b55', background: 'rgba(240,185,11,0.08)' }}>
            {status === 'done' ? <FiCheck /> : busy ? <FiLoader className="fin-spin" /> : <FiStar />} {label}
          </button>
        </span>
      ) : (
        // BINANCE PAY
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 3, maxWidth: 260 }}>
          <span style={{ fontSize: '0.64rem', color: 'var(--mk-text-secondary, #5a7a96)' }}>
            Pagá <b>{cfg.price} USD</b> por Binance Pay a: <b style={{ color: 'var(--mk-text-primary)' }}>{cfg.binancePay?.id}</b>
          </span>
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <input value={reference} onChange={e => setRef(e.target.value)} placeholder="ID de tu orden de Binance Pay" disabled={busy}
              style={{ flex: 1, minWidth: 0, fontSize: '0.66rem', padding: '4px 8px', borderRadius: 6, border: '1px solid var(--mk-border, #e2e8f0)', background: 'var(--mk-bg-card, #fff)', color: 'var(--mk-text-primary)' }} />
            <button className="fin-chip" onClick={submitBinance} disabled={busy} style={{ cursor: busy ? 'wait' : 'pointer', color: '#f0b90b', borderColor: '#f0b90b55', background: 'rgba(240,185,11,0.08)' }}>
              {status === 'bp-sending' ? <FiLoader className="fin-spin" /> : <FiCheck />} Ya pagué
            </button>
          </span>
        </span>
      )}
      {error && <span style={{ fontSize: '0.62rem', color: 'var(--fin-down)' }}>{error}</span>}
    </span>
  );
}
